import { useDraggable } from '@dnd-kit/core';
import { Button, styled, type SxProps, type Theme } from '@mui/material';
import { type PropsWithChildren } from 'react';

import { ELayoutManagerMode } from '#shared';

import { useLayoutEditing } from '../../Settings';
import {
  widgetRegistry,
  type WidgetId,
  type WidgetType,
} from '../../widgetManager';

const PREFIX = 'TSMED-draggableItem';
const draggableItemClasses = {
  ['root']: `${PREFIX}-root`,
};

export const DraggableItemRoot = styled(Button, {
  shouldForwardProp: prop =>
    !['$isDragging', '$isSelected', '$enableDrag', '$isPendingTarget'].includes(
      prop as string,
    ),
})<{
  $isDragging?: boolean;
  $isSelected?: boolean;
  $enableDrag?: boolean;
  $isPendingTarget?: boolean;
}>(
  ({
    $isDragging,
    $isSelected,
    $enableDrag,
    $isPendingTarget,
    theme: { palette, spacing },
  }) => ({
    [`&.${draggableItemClasses.root}`]: {
      position: 'relative',
      backgroundColor: palette.mapBlue[700],
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: spacing(1),
      cursor: $isDragging ? 'grabbing' : $enableDrag ? 'grab' : 'pointer',
      opacity: $isDragging ? 0.5 : 1,
      padding: spacing(1),
      transition:
        'background-color 0.2s, box-shadow 0.2s, border-color 0.2s, transform 0.2s',
      zIndex: 100,
      border: `1px solid ${palette.mapBlue[600]}`,
      borderColor: $isSelected
        ? palette.primary.light
        : $isPendingTarget
          ? palette.mapBlue[300]
          : palette.mapBlue[600],
      // Highlight effect when a section is pending (BlockAddButton clicked)
      boxShadow: $isPendingTarget
        ? `0 0 15px ${palette.mapBlue[300]}80, inset 0 0 10px ${palette.mapBlue[300]}30`
        : $isSelected
          ? `0 0 20px ${palette.primary.main}80`
          : 'none',
      // Pulsing animation keyframes for pending state
      animation: $isPendingTarget
        ? 'pulse-pending 1.5s ease-in-out infinite'
        : 'none',
      // Change overflow to visible to prevent clipping of indicators
      overflow: 'visible',

      // Hover effect for better visual feedback
      '&:hover': {
        backgroundColor: palette.mapBlue[600],
        borderColor: $isSelected
          ? palette.primary.light
          : $isPendingTarget
            ? palette.mapBlue[200]
            : palette.mapBlue[400],
        boxShadow: $isPendingTarget
          ? `0 0 20px ${palette.mapBlue[300]}A0, inset 0 0 12px ${palette.mapBlue[300]}40`
          : $isSelected
            ? `0 0 24px ${palette.primary.main}A0`
            : `0 4px 12px rgba(0, 0, 0, 0.3), 0 0 8px ${palette.mapBlue[400]}60`,
        transform: 'translateY(-2px)',
      },
    },

    // Pulsing animation keyframes for pending state
    '@keyframes pulse-pending': {
      '0%, 100%': {
        boxShadow: `0 0 15px ${palette.mapBlue[300]}80, inset 0 0 10px ${palette.mapBlue[300]}30`,
      },
      '50%': {
        boxShadow: `0 0 25px ${palette.mapBlue[300]}B0, inset 0 0 15px ${palette.mapBlue[300]}50`,
      },
    },
  }),
);

export const DraggableItem = ({
  id,
  children,
  sx,
}: PropsWithChildren<{ id: WidgetId | WidgetType; sx?: SxProps<Theme> }>) => {
  const {
    selectedPaletteWidget,
    mode,
    setSelectedWidgetId,
    selectedWidgetId,
    setSelectedPaletteWidget,
    pendingPlacement,
  } = useLayoutEditing();
  const readOnly = mode === ELayoutManagerMode.VIEW;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: readOnly,
  });

  // Extract base widget type from variant IDs (e.g., 'geo-map-center' -> 'geo-map')
  const getBaseWidgetType = (variantId: string): WidgetType => {
    if (variantId === 'geo-map-center' || variantId === 'geo-map-focus') {
      return 'geo-map';
    }
    return variantId as WidgetType;
  };

  const baseWidgetType = getBaseWidgetType(id as string);

  // Check if this is a palette widget (registered in widgetRegistry, not an instance)
  const isPaletteWidget = widgetRegistry.has(baseWidgetType);
  const isSelected = isPaletteWidget && selectedPaletteWidget === id;

  // Check if a section/BlockAddButton is pending (waiting for widget selection)
  // Highlight all palette widgets when pending to show they can be clicked
  const isPendingTarget =
    isPaletteWidget &&
    pendingPlacement !== null &&
    mode === ELayoutManagerMode.EDIT;

  const handleClick = (e: React.MouseEvent | React.PointerEvent) => {
    if (readOnly || !isPaletteWidget) {
      return;
    }

    e.stopPropagation();

    // Clear widget selection when selecting palette widget
    if (selectedWidgetId) {
      setSelectedWidgetId(null);
    }

    // Always delegate to setSelectedPaletteWidget for all placement/selection logic
    // It will handle pendingPlacement internally
    // Toggle selection: if already selected, deselect; otherwise select
    // Pass the full id (including variant like 'geo-map-center')
    setSelectedPaletteWidget(isSelected ? null : (id as string));
  };

  // For palette widgets, use onClick for selection
  // Drag timing is handled by DndContext's distance-based activation (10px)
  const customListeners = isPaletteWidget
    ? {
        onClick: handleClick,
      }
    : {};

  return (
    <DraggableItemRoot
      className={draggableItemClasses.root}
      ref={setNodeRef}
      sx={{
        // This keeps the original element in place for correct hit detection
        opacity: readOnly ? 0.5 : isDragging ? 0.5 : 1,
        cursor: readOnly ? 'not-allowed' : undefined,
        // Hide the original element visually when dragging (DragOverlay shows the preview)
        visibility: isDragging ? 'hidden' : 'visible',
        ...sx,
      }}
      $isDragging={isDragging}
      $isSelected={isSelected}
      $enableDrag={(!isPaletteWidget || isSelected) && !readOnly}
      $isPendingTarget={isPendingTarget}
      {...listeners}
      {...attributes}
      {...customListeners}
    >
      {children}
    </DraggableItemRoot>
  );
};
